import { useEffect, useState } from "react";
import { SettingsSection, StatusText } from "./SettingsSection";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Input } from "../ui";

interface TokenRow {
  id: string;
  name: string;
  scopes: string[];
  createdAt: string;
  lastSeenAt: string | null;
}

const PRESETS: Record<string, string[]> = {
  read: ["tasks:read", "tracking:read", "dashboards:read", "config:read"],
  tasks: ["tasks:read", "tasks:write", "tracking:read", "tracking:write"],
  // phones, chat bots, buttons: may add to the tray or create a task, nothing else (I0)
  capture: ["capture:write"],
  // Home Assistant sensors (I7): read only
  homeAssistant: ["tasks:read", "tracking:read"],
  // scripts and sensors that report outside facts (I8)
  signals: ["signals:write"],
  all: ["*"],
};

/**
 * Settings → Access (spec §9.11, level `power`): tokens for agents and scripts, with explicit scopes.
 * The plaintext token is shown once, right after it was created.
 */
export function AccessSettings() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<TokenRow[]>([]);
  const [allScopes, setAllScopes] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>(PRESETS.tasks);
  const [fresh, setFresh] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = () =>
    fetch("/api/internal/tokens", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { tokens: [], scopes: [] }))
      .then((b: { tokens: TokenRow[]; scopes: string[] }) => {
        setRows(b.tokens);
        setAllScopes(b.scopes);
      });
  useEffect(() => {
    void refresh();
  }, []);

  const send = (method: string, body: Record<string, unknown>) =>
    fetch("/api/internal/tokens", {
      method,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

  const create = async () => {
    const r = await send("POST", { name: name.trim(), scopes });
    if (!r.ok) return;
    setFresh(((await r.json()) as { token: string }).token);
    setCopied(false);
    setName("");
    void refresh();
  };

  const toggle = (scope: string, on: boolean) => {
    const current = scopes.includes("*") ? allScopes : scopes;
    setScopes(on ? [...current, scope] : current.filter((s) => s !== scope));
  };
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });

  return (
    <SettingsSection
      id="settings-access"
      intro={t("access.hint")}
      summary={t("settings.summary.access")}
      title={t("access.title")}
      testId="settings-access"
      folded
    >
      {rows.length === 0 ? (
        <p className="text-[13px] text-neutral-500">{t("access.none")}</p>
      ) : (
        <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-2 py-2" data-testid="access-row">
              <span className="text-[13.5px] font-medium">{r.name}</span>
              <StatusText tone="quiet">{r.scopes.join(", ")}</StatusText>
              <span className="ml-auto text-[11.5px] text-neutral-500">
                {r.lastSeenAt
                  ? t("access.lastSeen", { when: fmt.format(new Date(r.lastSeenAt)) })
                  : t("access.never")}
              </span>
              <Button
                variant="secondary"
                size="sm"
                aria-label={t("access.revokeLabel", { name: r.name })}
                onClick={() => void send("DELETE", { id: r.id }).then(refresh)}
              >
                {t("access.revoke")}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {fresh && (
        <div className="flex flex-col gap-2 rounded-md border border-accent/40 bg-accent/5 p-3" role="status">
          <p className="text-[12.5px]">{t("access.shownOnce")}</p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 break-all font-mono text-[12px]" data-testid="access-token">
              {fresh}
            </code>
            <Button
              size="sm"
              onClick={() => {
                void navigator.clipboard?.writeText(fresh);
                setCopied(true);
              }}
            >
              {copied ? t("access.copied") : t("access.copy")}
            </Button>
          </div>
          <p className="font-mono text-[11.5px] text-neutral-500">
            {t("access.mcpHint", { url: `${window.location.origin}/mcp` })}
          </p>
        </div>
      )}
      <div className="flex flex-col gap-2 border-t border-neutral-200 pt-3 dark:border-neutral-800">
        <Input
          aria-label={t("access.name")}
          placeholder={t("access.namePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="access-name"
        />
        <div className="flex flex-wrap gap-2">
          {Object.keys(PRESETS).map((k) => (
            <Button key={k} size="sm" variant="secondary" onClick={() => setScopes(PRESETS[k])}>
              {t(`access.preset.${k}`)}
            </Button>
          ))}
        </div>
        <fieldset className="grid grid-cols-2 gap-x-4 gap-y-1">
          <legend className="mb-1 text-[11.5px] text-neutral-500">{t("access.scopesLabel")}</legend>
          {allScopes.map((s) => (
            <span key={s} className="flex items-center gap-2">
              <Checkbox
                checked={scopes.includes(s) || scopes.includes("*")}
                label={s}
                onChange={(v) => toggle(s, v)}
              />
              <span className="font-mono text-[12px]" aria-hidden>
                {s}
              </span>
            </span>
          ))}
        </fieldset>
        <div>
          <Button
            onClick={() => void create()}
            disabled={!name.trim() || scopes.length === 0}
            data-testid="access-create"
          >
            {t("access.create")}
          </Button>
        </div>
        <p className="text-[11.5px] text-neutral-500">{t("access.docs")}</p>
      </div>
    </SettingsSection>
  );
}
